import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Kbd } from '@/components/ui/kbd';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { TopNavBar } from '../../../common/Header/TopNavBar';
import { Connection, HomeSummary, useHomeApi } from '../../../common/hooks/use-home-api.hook';
import { Insight, InsightQueue, useInsightsApi } from '../../../common/hooks/use-insights-api.hook';
import { TeamRosterEntry, useAssistantApi } from '../../../common/hooks/use-assistant-api.hook';
import { useAskAi } from '../../../modules/vendor/assistant/AskAiContext';
import { SHELL_HEIGHT } from '../../../modules/vendor/assistant/askai-layout';
import { useAgentWork } from '../../../modules/vendor/work/use-agent-work.hook';
import { useAgentWorkSignal } from '../../../modules/vendor/work/agent-work.store';
import { WorkSheet } from '../../../modules/vendor/work/WorkSheet';
import { SidePanel } from '../../../components/design-system/common/SidePanel';
import { InsightsPanel } from '../../../modules/vendor/insights/components/InsightsPanel';
import { actionRoute, findingText, relativeTime } from '../../../modules/vendor/insights/insight-presentation';
import { agentName } from '../../../modules/vendor/agents/agents';
import { AgentAvatar } from '../../../modules/vendor/agents/components/AgentAvatar';
import { Composer } from '../../../modules/vendor/agents/components/Composer';
import { ArdiLogPanel, ArdiStatusPill, useArdiStatus } from '../../../modules/vendor/ardi/ArdiPulse';
import { Connections } from '../../../modules/vendor/agents/components/Connections';
import { ConnectionsStrip } from '../../../modules/vendor/agents/components/ConnectionsStrip';
import { AGENT_TEMPLATES, AgentTemplate, templateKey } from '../../../modules/vendor/agents/templates';
import { StartHere, useStartHere } from '../../../modules/vendor/onboarding/StartHere';
import { useDecisionCount } from '../../../modules/vendor/decisions/use-decision-count';
import {
    HomeCard,
    greetingKey,
    homeChips,
    homeRow,
    pickSuggestions,
    suggestionBrief,
    suggestionFigure,
    suggestionOwner,
} from '../../../modules/vendor/agents/home-feed';

/**
 * Home: one screen, one question.
 *
 * A greeting and a line on what the team did; the composer, with what the
 * team can see under it; the counts of what is waiting; and one row of three —
 * the most urgent things the team noticed, and a ready-made agent to start.
 * Nothing to scroll. Everything else is one click away: every finding in a
 * panel, every agent and the team on the AI team page, past conversations
 * under Recents, and the old charts on Overview.
 */

const SEVERITY_DOT: Record<string, string> = {
    critical: 'bg-destructive',
    warning: 'bg-warning',
    info: 'bg-muted-foreground/40',
};

/** The shell of every card in the row, so a finding and an agent sit level. */
const CardShell: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
    <article className={cn('group flex h-full flex-col gap-2 rounded-2xl border bg-card p-4 transition-colors hover:border-foreground/25', className)}>
        {children}
    </article>
);

const AgentHomePage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const askAi = useAskAi();
    const { user } = useAuthContext();
    const home = useHomeApi();
    const insightsApi = useInsightsApi();
    const assistantApi = useAssistantApi();
    const work = useAgentWork(20);
    const finishedAt = useAgentWorkSignal((s) => s.finishedAt);

    const [summary, setSummary] = useState<HomeSummary | null>(null);
    const [queue, setQueue] = useState<InsightQueue | null>(null);
    const [team, setTeam] = useState<TeamRosterEntry[] | null>(null);
    const [connections, setConnections] = useState<Connection[] | null>(null);
    const [workOpen, setWorkOpen] = useState(false);
    const [findingsOpen, setFindingsOpen] = useState(false);
    const [connectionsOpen, setConnectionsOpen] = useState(false);
    const [ardiOpen, setArdiOpen] = useState(false);
    const { status: ardiStatus } = useArdiStatus();
    const [dismissed, setDismissed] = useState<Set<string>>(new Set());
    // A ready-made agent's brief, handed to the composer — from a card here,
    // or from the gallery on the AI team page (router state).
    const [seed, setSeed] = useState<{ text: string; nonce: number } | null>(null);
    const composerRef = useRef<HTMLDivElement>(null);
    const startHere = useStartHere();
    // Everything waiting for a yes, across Ardealy (feat/room-to-revenue): one chip, the inbox one click away.
    const decisions = useDecisionCount();

    useEffect(() => {
        home.getSummary().then(setSummary).catch(() => setSummary(null));
        home.getConnections().then(setConnections).catch(() => setConnections([]));
        void askAi?.refreshThreads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Links from a notification or an email open a panel straight away:
    // ?panel=findings (the weekly review), ?panel=ardi (Ardi's log).
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const panel = params.get('panel');
        if (panel === 'findings') setFindingsOpen(true);
        if (panel === 'ardi') setArdiOpen(true);
        if (panel) {
            params.delete('panel');
            navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true, state: location.state });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    useEffect(() => {
        const brief = (location.state as { seed?: string } | null)?.seed;
        if (!brief) return;
        setSeed({ text: brief, nonce: Date.now() });
        // Consumed once: a refresh must not put it back.
        navigate(location.pathname, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    // Re-read whenever a run finishes: a report can resolve a finding, and the
    // status line should stop saying "working" the moment it stops.
    useEffect(() => {
        insightsApi.getQueue({ limit: 20 }).then(setQueue).catch(() => setQueue(null));
        assistantApi.getTeam().then(setTeam).catch(() => setTeam([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finishedAt]);

    const suggestions = useMemo(
        () => pickSuggestions((queue?.items ?? []).filter((i) => !dismissed.has(i.findingId))),
        [queue, dismissed],
    );
    const row = useMemo(() => homeRow(suggestions, AGENT_TEMPLATES, connections), [suggestions, connections]);

    const attention = (queue?.counts.critical ?? 0) + (queue?.counts.warning ?? 0);
    const chips = homeChips({
        approvals: work.waiting.length,
        attention,
        leads: summary?.workspaces.visualizer?.leads.value ?? null,
        working: work.running.length,
    });

    const watch = team?.find((m) => m.id === 'watch');

    const ask = (text: string) => {
        void askAi?.startChat(text);
        navigate('/home/chat');
    };

    const workOn = (finding: Insight) => {
        const title = findingText(t, finding.title, { key: finding.i18n?.title, params: finding.i18n?.params });
        const detail = findingText(t, finding.detail, { key: finding.i18n?.detail, params: finding.i18n?.params });
        ask(suggestionBrief(title, detail, t('AGENTS.HOME.WORK_ON_IT_ASK', 'Look into this with the team and tell me what to do about it:') as string));
    };

    const startFromTemplate = (template: AgentTemplate) => {
        setSeed({ text: String(t(`${templateKey(template.id)}.PROMPT`)), nonce: Date.now() });
        composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const dismiss = async (finding: Insight) => {
        setDismissed((prev) => new Set(prev).add(finding.findingId));
        try {
            await insightsApi.acknowledge(finding.findingId);
        } catch {
            setDismissed((prev) => { const next = new Set(prev); next.delete(finding.findingId); return next; });
        }
    };

    const timeAgo = (iso?: string) => {
        if (!iso) return '';
        const r = relativeTime(iso);
        return t(r.key, { count: r.count });
    };

    const firstName = (user as any)?.firstname ?? '';

    /** The line under the greeting: what the team did, then what is waiting. */
    const subtitle = (() => {
        const checks = watch?.last?.result?.checks;
        const did = watch?.last && checks
            ? t('AGENTS.HOME.WATCH_LINE', {
                checks,
                metrics: watch.last.result?.metricsRead ?? 0,
                when: timeAgo(watch.last.at),
                defaultValue: 'Your team ran {{checks}} checks across {{metrics}} numbers {{when}}.',
            })
            : t('AGENTS.HOME.READY_LINE', 'Your team is ready.');
        const waiting = attention
            ? t('AGENTS.HOME.WAITING_LINE', { count: attention, defaultValue: '{{count}} things need you.' })
            : t('AGENTS.HOME.CLEAR_LINE', 'Nothing urgent.');
        return `${did} ${waiting}`;
    })();

    const chipAction = (id: string) => {
        switch (id) {
            case 'working':
            case 'approve':
                setWorkOpen(true);
                break;
            case 'attention':
                setFindingsOpen(true);
                break;
            case 'leads':
                navigate('/visualizer/engagement/leads');
                break;
        }
    };

    const findingCard = (finding: Insight) => {
        const owner = suggestionOwner(finding);
        const figure = suggestionFigure(finding);
        const route = actionRoute(finding);
        const title = findingText(t, finding.title, { key: finding.i18n?.title, params: finding.i18n?.params });
        const detail = findingText(t, finding.detail, { key: finding.i18n?.detail, params: finding.i18n?.params });

        return (
            <CardShell key={finding.findingId}>
                <div className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                        <AgentAvatar agent={owner} size="sm" />
                        <span className="truncate text-xs text-muted-foreground">{agentName(t, owner)}</span>
                        <span className={cn('size-1.5 shrink-0 rounded-full', SEVERITY_DOT[finding.severity])} aria-hidden />
                    </span>
                    <span className="flex items-center gap-2">
                        {figure && <span className="text-lg font-medium tracking-tight tabular-nums">{figure}</span>}
                        <button
                            type="button"
                            onClick={() => void dismiss(finding)}
                            aria-label={t('AGENTS.HOME.DISMISS', 'Dismiss') as string}
                            className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-foreground"
                        >
                            <X className="size-3.5" />
                        </button>
                    </span>
                </div>
                <h3 className="line-clamp-2 text-sm font-medium leading-snug">{title}</h3>
                <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{detail}</p>
                <div className="mt-auto flex items-center gap-1.5 pt-1">
                    <Button variant="outline" size="sm" onClick={() => workOn(finding)}>
                        {t('AGENTS.HOME.WORK_ON_IT', 'Work on it')}
                    </Button>
                    {route && (
                        <Button variant="ghost" size="sm" onClick={() => navigate(route)} className="text-muted-foreground">
                            {t('AGENTS.HOME.OPEN', 'Open')}
                            <ArrowUpRight data-icon="inline-end" />
                        </Button>
                    )}
                </div>
            </CardShell>
        );
    };

    const templateCard = (template: AgentTemplate) => {
        const key = templateKey(template.id);
        return (
            <CardShell key={template.id}>
                <div className="flex items-center justify-between gap-3">
                    <span className="flex -space-x-1.5 rtl:space-x-reverse">
                        {template.agents.map((a) => <AgentAvatar key={a} agent={a} size="sm" />)}
                    </span>
                    <span className="text-xs text-muted-foreground">{t('AGENTS.HOME.READY_MADE', 'Ready-made agent')}</span>
                </div>
                <h3 className="line-clamp-2 text-sm font-medium leading-snug">{t(`${key}.TITLE`)}</h3>
                <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{t(`${key}.DESC`)}</p>
                <div className="mt-auto flex items-center gap-1.5 pt-1">
                    <Button variant="outline" size="sm" onClick={() => startFromTemplate(template)}>
                        {t('AGENTS.HOME.USE', 'Use')}
                    </Button>
                </div>
            </CardShell>
        );
    };

    const card = (c: HomeCard) => (c.kind === 'finding' ? findingCard(c.finding) : templateCard(c.template));

    return (
        // The shell's height, so the content can be centred in what is left
        // under the bar — Home is one screen, not a page to scroll.
        <div className="flex flex-col" style={{ minHeight: SHELL_HEIGHT }}>
            <TopNavBar breadcrumbs={[{ label: t('AGENTS.NAV.HOME', 'Home') }]} />

            <div className="flex flex-1 flex-col justify-center gap-8 px-4 py-8">
                <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-5">
                    <div className="flex flex-col items-center gap-2 text-center">
                        <ArdiStatusPill status={ardiStatus} onOpen={() => setArdiOpen(true)} />
                        <h1 className="text-3xl font-medium tracking-tight text-balance md:text-4xl">
                            {t(`AGENTS.HOME.GREETING.${greetingKey()}`, { name: firstName, defaultValue: 'Welcome back, {{name}}.' })}
                        </h1>
                        <p className="text-sm text-muted-foreground">{team ? subtitle : ' '}</p>
                    </div>

                    <div ref={composerRef} className="w-full">
                        <Composer
                            variant="hero"
                            onSubmit={ask}
                            autoFocus
                            seed={seed}
                            onRecents={askAi?.threads.length ? () => navigate('/home/chat') : undefined}
                        />
                        <ConnectionsStrip connections={connections} onOpen={() => setConnectionsOpen(true)} />
                    </div>

                    <div className="flex min-h-8 flex-wrap items-center justify-center gap-2">
                        {!!decisions?.total && (
                            <button
                                type="button"
                                onClick={() => navigate('/home/decisions')}
                                data-testid="home-decisions-chip"
                                className="flex items-center gap-2 rounded-full border border-foreground/40 bg-background py-1 ps-3 pe-1 text-xs transition-colors hover:border-foreground/60 hover:bg-accent"
                            >
                                {!!decisions.now && <span className="size-1.5 rounded-full bg-warning" aria-hidden />}
                                {t('DECISIONS.HOME_CHIP', { count: decisions.total })}
                                <span className="min-w-5 rounded-full bg-muted px-1.5 py-0.5 text-center text-[11px] font-medium tabular-nums">{decisions.total}</span>
                            </button>
                        )}
                        {chips.map((chip) => (
                            <button
                                key={chip.id}
                                type="button"
                                onClick={() => chipAction(chip.id)}
                                className="flex items-center gap-2 rounded-full border bg-background py-1 ps-3 pe-1 text-xs transition-colors hover:border-foreground/30 hover:bg-accent"
                            >
                                {chip.id === 'working' && <span className="size-1.5 animate-pulse rounded-full bg-success" aria-hidden />}
                                {t(`AGENTS.HOME.CHIP.${chip.id.toUpperCase()}`)}
                                <span className="min-w-5 rounded-full bg-muted px-1.5 py-0.5 text-center text-[11px] font-medium tabular-nums">{chip.count}</span>
                            </button>
                        ))}
                        <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex">
                            {t('AGENTS.HOME.JUMP', 'Jump anywhere')} <Kbd>⌘K</Kbd>
                        </span>
                    </div>
                </div>

                {/* Fresh from onboarding: the chosen workspaces, each with its
                    first step, in place of the suggestions until dismissed —
                    one row either way, so Home stays one screen. */}
                {startHere.visible ? (
                    <StartHere goals={startHere.goals} onHide={startHere.hide} />
                ) : (
                /* One row: what the team noticed, and something to start. */
                <section className="mx-auto w-full max-w-4xl">
                    <div className="mb-2.5 flex items-center justify-between gap-3">
                        <h2 className="text-sm font-medium">{t('AGENTS.HOME.PICK_UP', 'Suggested for you')}</h2>
                        <span className="flex items-center gap-1">
                            {!!queue?.items.length && (
                                <Button variant="ghost" size="sm" onClick={() => setFindingsOpen(true)} className="text-muted-foreground">
                                    {t('AGENTS.HOME.ALL_FINDINGS', 'All findings')}
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => navigate('/home/agents')} className="text-muted-foreground">
                                {t('AGENTS.HOME.ALL_AGENTS', 'All agents')}
                                <ArrowRight data-icon="inline-end" className="rtl:rotate-180" />
                            </Button>
                        </span>
                    </div>
                    <div className="grid gap-3 md:grid-cols-3">
                        {!queue || !connections
                            ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)
                            : row.map(card)}
                    </div>
                </section>
                )}
            </div>

            <WorkSheet open={workOpen} onOpenChange={setWorkOpen} />
            <ArdiLogPanel open={ardiOpen} onOpenChange={setArdiOpen} status={ardiStatus} />
            <SidePanel
                open={connectionsOpen}
                onOpenChange={setConnectionsOpen}
                title={t('AGENTS.CONNECT.TITLE', 'Connections') as string}
            >
                <Connections connections={connections} layout="list" />
            </SidePanel>
            <SidePanel
                open={findingsOpen}
                onOpenChange={setFindingsOpen}
                title={t('AGENTS.HOME.FINDINGS_TITLE', 'What your team noticed') as string}
            >
                <InsightsPanel layout="grid" limit={30} showSeeAll={false} />
            </SidePanel>
        </div>
    );
};

export default AgentHomePage;
